import { ISymphonyApiConfig } from "../../configs/ISymphonyApiConfig";
import { MyCAIApiService } from "../MyCAIApiService";
import { paramBuilder, ParamBuilder } from "../../utilities/uri";

export class SymphonyApiServicev3 extends MyCAIApiService {
    public Assistants: AssistantsService;
    public Chats: ChatsService;
    public DataSources: DataSourcesService;
    public Facts: FactsService;
    public Integrations: IntegrationsService;
    public Licensing: LicensingService;
    public Messages: MessagesService;
    public Models: ModelsService;
    public QuickActions: QuickActionsService;
    public UserProfiles: UserProfilesService;
    public DataStore: DataStoreService
    public Templates: TemplateService;
    public Resources: ResourcesService;
    public paramBuilder: ParamBuilder;

    constructor(config: ISymphonyApiConfig) {
        super(config);
        this.paramBuilder = paramBuilder;

        this.Assistants = new AssistantsService(this);
        this.Chats = new ChatsService(this, this.paramBuilder);
        this.DataSources = new DataSourcesService(this);
        this.Facts = new FactsService(this);
        this.Integrations = new IntegrationsService(this);
        this.Licensing = new LicensingService(this);
        this.Messages = new MessagesService(this);
        this.Models = new ModelsService(this);
        this.QuickActions = new QuickActionsService(this, this.paramBuilder);
        this.UserProfiles = new UserProfilesService(this, this.paramBuilder);
        this.DataStore = new DataStoreService(this, this.paramBuilder);
        this.Templates = new TemplateService(this, this.paramBuilder);
        this.Resources = new ResourcesService(this);
    }
}

class ChatsService {
    private apiService: SymphonyApiServicev3;
    private paramBuilder: ParamBuilder;
    constructor(apiService: SymphonyApiServicev3, paramBuilder: ParamBuilder) {
        this.apiService = apiService;
        this.paramBuilder = paramBuilder
    }

    public async getChats(expand: string[] | undefined, take: number | undefined, skip: number | undefined, takeAll: boolean | undefined): Promise<any> {
        const endpoint = `/maestro/v3/Chats${this.paramBuilder({
            $expand: expand,
            $top: take,
            $skip: skip,
            takeAll: takeAll
        })}`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async createChat(opt: object | undefined): Promise<any> {
        const endpoint = `/maestro/v3/Chats`;
        return this.apiService.sendRequest({
            method: "post",
            endpoint,
            data: opt ?? {}
        });
    }

    public async getChat(chatId: string, expand: string[] | undefined): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}${this.paramBuilder({
            $expand: expand
        })}`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async searchChats(query: string, expand: string[] | undefined, take: number | undefined, skip: number | undefined, takeAll: boolean | undefined): Promise<any> {
        const endpoint = `/maestro/v3/Chats${this.paramBuilder({
            $filter: `contains(Name, '${query}')`,
            $expand: expand,
            $top: take,
            $skip: skip,
            takeAll: takeAll
        })}`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    // Chat Functions
    public async pinChat(chatId: string, pinPosition: number | undefined): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}/pin`;
        return this.apiService.sendRequest({
            method: "patch",
            endpoint,
            data: {
                toPin: true,
                pinPosition: pinPosition
            }
        });
    }

    public async unpinChat(chatId: string): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}/pin`;
        return this.apiService.sendRequest({
            method: "patch",
            endpoint,
            data: {
                toPin: false
            }
        });
    }

    public async renameChat(chatId: string, name: string): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}`;
        return this.apiService.sendRequest({
            method: "patch",
            endpoint,
            data: {
                name: name
            }
        });
    }

    public async deleteChat(chatId: string): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}`;
        return this.apiService.sendRequest({
            method: "delete",
            endpoint,
        });
    }

    public async getChatRecommendations(chatId: string): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}/recommendations`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async getChatQuickActions(chatId: string): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}/quickActions`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }



    public async updateChatModel(
        chatId: string,
        model: object
    ): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}/model`;
        return this.apiService.sendRequest({
            method: "patch",
            endpoint,
            data: model
        });
    }

    public async updateAssistantInstruction(
        chatId: string,
        assistantId: string | undefined,
        assistantInstruction: string | undefined
    ): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}/assistant`;
        return this.apiService.sendRequest({
            method: "patch",
            endpoint,
            data: {
                assistantInstructionId: assistantId,
                systemMessage: assistantInstruction
            }
        });
    }

    public async updateChatModelParameter(
        chatId: string,
        modelId: string,
        modelParameterId: number,
        value: number
    ): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}/modelParameter`;
        return this.apiService.sendRequest({
            method: "post",
            endpoint,
            data: {
                chatId: chatId,
                modelId: modelId,
                modelParameterId: modelParameterId,
                value: value
            }
        });
    }

    // Chat Sharing
    public async shareChat(chatId: string, userId: string | undefined, userIdentifier: string | undefined, accessLevel : number = 3): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}/access`;
        return this.apiService.sendRequest({
            method: "post",
            endpoint,
            data: {
                chatId: chatId,
                userProfileId: userId,
                userProfile: {
                    identifier: userIdentifier
                },
                membership: accessLevel
            },
        });
    }

    public async updateChatAccess(chatId: string, accessLevel: number | undefined = 3, userId: string | undefined, userIdentifier: string | undefined): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}/access`;
        return this.apiService.sendRequest({
            method: "patch",
            endpoint,
            data: {
                chatId: chatId,
                userProfileId: userId,
                userProfile: {
                    identifier: userIdentifier
                },
                membership: accessLevel
            },
        });
    }

    public async unshareChat(chatId: string, userId: string): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}/access/${userId}`;
        return this.apiService.sendRequest({
            method: "delete",
            endpoint,
        });
    }

    public async leaveSharedChat(chatId: string): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}/access/me`;
        return this.apiService.sendRequest({
            method: "delete",
            endpoint,
        });
    }

    // Chat Resources
    public async addChatResource(chatId: string, file: any): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}/Resources`;
        return this.apiService.sendPollingRequest({
            method: "post",
            endpoint,
            data: file,
            pollingInterval: 500,
            maxPollingDuration: 900000
        });
    }

    public async getChatResources(chatId: string): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}/resources`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async deleteChatResource(
        chatId: string,
        resourceId: string
    ): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}/resources/${resourceId}`;
        return this.apiService.sendRequest({
            method: "delete",
            endpoint,
        });
    }



    //Chat Datastores
    public async EnableDatastore(
        chatId: string,
        dataCollectionId: string
    ): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}/datacollection/${dataCollectionId}`;
        return this.apiService.sendRequest({
            method: "post",
            endpoint
        });
    }

    public async DisableDataStore(
        chatId: string,
        dataCollectionId: string
    ): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}/datacollection/${dataCollectionId}`;
        return this.apiService.sendRequest({
            method: "delete",
            endpoint
        });
    }

    public async GetChatDataStores(
        chatId: string
    ): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}/datacollection`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint
        });
    }

    public async ExportChat(
        chatId: string
    ): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}/export`;
        return this.apiService.sendPollingRequest({
            method: "post",
            endpoint,
            pollingInterval: 500
        })
    }

    // Chat Skills
    public async GetChatSkills(
        chatId: string
    ): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}/aiskills`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }
}

class MessagesService {
    private apiService: SymphonyApiServicev3;

    constructor(apiService: SymphonyApiServicev3) {
        this.apiService = apiService;
    }

    public async sendMessage(
        chatId: string,
        message: string,
        assistantInstruction: object | undefined,
        model: object | undefined,
        messageParameters: object[] | undefined,
        dataSources: string[] | undefined,
        includedSkills: string[] | undefined,
        messageTextOverride: string | undefined,
        dataStores: string[] | undefined
    ): Promise<any> {
        const endpoint = `/maestro/v3/Chats/${chatId}/Messages`;
        const data = {
            MessageText: message,
            AssistantInstruction: assistantInstruction,
            Model: model,
            MessageParameters: messageParameters,
            DataSources: dataSources,
            Skills: includedSkills,
            MessageTextOverride: messageTextOverride,
            EnabledDataStores: dataStores
        };

        return this.apiService.sendPollingRequest({
            method: "post",
            endpoint,
            data,
        });
    }

    public async deleteMessage(messageId: string): Promise<any> {
        const endpoint = `/maestro/v3/messages/${messageId}`;
        return this.apiService.sendRequest({
            method: "delete",
            endpoint,
        });
    }

    public async rateMessage(
        messageId: string,
        rating: boolean
    ): Promise<any> {
        const endpoint = `/maestro/v3/Messages/${messageId}/rating`;
        return this.apiService.sendRequest({
            method: "patch",
            endpoint,
            data: rating
        });
    }

    public async getMessageDetails(
        messageId: string
    ): Promise<any> {
        const endpoint = `/maestro/v3/Messages/${messageId}/details`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async getMessageAuditTrail(
        messageId: string
    ): Promise<any> {
        const endpoint = `/maestro/v3/Messages/${messageId}/audit`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async getMessageTransactions(
        messageId: string
    ): Promise<any> {
        const endpoint = `/maestro/v3/Messages/${messageId}/transactions`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }
}

class UserProfilesService {
    private apiService: SymphonyApiServicev3;
    private paramBuilder: ParamBuilder;
    constructor(apiService: SymphonyApiServicev3, paramBuilder: ParamBuilder) {
        this.apiService = apiService;
        this.paramBuilder = paramBuilder
    }

    public async getLoggedInUserProfile(expand: string[]): Promise<any> {
        const endpoint = `/framework/v3/UserProfiles/me${this.paramBuilder({ $expand: expand }) }`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async syncLoggedInUserProfile(): Promise<any> {
        const endpoint = `/framework/v3/UserProfiles/me`;
        return this.apiService.sendRequest({
            method: "post",
            endpoint,
        });
    }

    public async getPublicUserProfile(userProfileId: string): Promise<any> {
        const endpoint = `/framework/v3/UserProfiles/${userProfileId}`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async getAdminLevel(): Promise<any> {
        const endpoint = `/framework/v3/UserProfiles/admin`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public buildUserSearchQuery(raw: string) {
        const tokens = raw.trim().replace(/\s+/g, ' ').split(' ');
        if (!tokens[0]) return '';

        const esc = (s: string) => s.replace(/'/g, "''").toLowerCase();

        const tokenClauses = tokens.map(t => {
            const q = esc(t);
            return [
            `contains(GivenName,'${q}')`,
            `contains(FamilyName,'${q}')`,
            `contains(Identifier,'${q}')`
            ].join(' or ');
        });

        return tokenClauses.map(c => `(${c})`).join(' and ');
    }

    public async searchUsers(query: string, take: number | undefined, skip: number | undefined, takeAll: boolean | undefined): Promise<any> {
        const endpoint = `/framework/v3/UserProfiles${this.paramBuilder(
            {
                oDataQuery: `?$filter=${this.buildUserSearchQuery(query)}&$top=${25}`,
                take: take,
                skip: skip,
                takeAll: takeAll
            }
        )}`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async createUserProfile(): Promise<any> {
        const endpoint = `/framework/v3/UserProfiles`;
        return this.apiService.sendRequest({
            method: "post",
            endpoint,
        });
    }

    public async updateUserProfile(
        userProfileId: string,
        updatedUserProfile: any
    ): Promise<any> {
        const endpoint = `/framework/v3/UserProfiles/${userProfileId}`;
        return this.apiService.sendRequest({
            method: "put",
            endpoint,
            data: updatedUserProfile
        });
    }

    public async saveUserProfileSettings(
        modelParams: any,
        model: string,
        assistantInstruction: string
    ): Promise<any> {
        const endpoint = `/framework/v3/UserProfiles/settings`;
        return this.apiService.sendRequest({
            method: "patch",
            endpoint,
            data: {
                model,
                assistantInstruction,
                modelParameters: modelParams,
            },
        });
    }

    public async updateUserAssistantInstruction(
        assistantId: string | undefined,
        assistantInstruction: string | undefined
    ): Promise<any> {
        const endpoint = `/framework/v3/UserProfiles/assistant`;
        return this.apiService.sendRequest({
            method: "post",
            endpoint,
            data: {
                assistantInstructionId: assistantId,
                systemMessage: assistantInstruction
            }
        });
    }

    public async updateUserDefaultModelParam(
        modelId: string,
        modelParameterId: number,
        value: number
    ): Promise<any> {
        const endpoint = `/framework/v3/UserProfiles/ModelParam`;
        return this.apiService.sendRequest({
            method: "post",
            endpoint,
            data: {
                modelId: modelId,
                modelParameterId: modelParameterId,
                value: value
            }
        });
    }

    // EULA
    public async getPendingEULA(): Promise<any> {
        const endpoint = `/framework/v3/UserProfiles/EULA`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async agreeToEULA(eulaId: string): Promise<any> {
        const endpoint = `/framework/v3/UserProfiles/EULA/${eulaId}`;
        return this.apiService.sendRequest({
            method: "post",
            endpoint,
        });
    }
}

class AssistantsService {
    private apiService: SymphonyApiServicev3;

    constructor(apiService: SymphonyApiServicev3) {
        this.apiService = apiService;
    }

    public async getAssistants(): Promise<any> {
        const endpoint = `/maestro/v3/Assistants`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }
}

class DataSourcesService {
    private apiService: SymphonyApiServicev3;

    constructor(apiService: SymphonyApiServicev3) {
        this.apiService = apiService;
    }

    public async getExternalDataSources(): Promise<any> {
        const endpoint = `/maestro/v3/DataSources`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }
}

class DataStoreService{
    private apiService: SymphonyApiServicev3;
    private paramBuilder: ParamBuilder;

    constructor(apiService: SymphonyApiServicev3, paramBuilder: ParamBuilder) {
        this.apiService = apiService;
        this.paramBuilder = paramBuilder;
    }

    public async getDataStores(search: string | undefined): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections${this.paramBuilder({ query: search })}`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async getDataStore(dataStoreId: string): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async createDataStore(dataStore: { name: string; description: string }): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections`;
        return this.apiService.sendRequest({
            method: "post",
            endpoint,
            data: dataStore
        });
    }

    // Removed the unnecessary param "dataStoreId: string"
    public async getDataStoreContentItem(contentItemId: string): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/content/${contentItemId}`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async removeDataStoreContent(dataStoreId: string, contentItemId: string, parentStorageId: string | null): Promise<any> {
        const endpoint = `maestro/v3/datacollections/${dataStoreId}/content/${contentItemId}
            ${this.paramBuilder({ parentStorageId: parentStorageId }) }`;
        return this.apiService.sendRequest({
            method: "delete",
            endpoint
        });
    }

    public async removeUserDataStoreAccess(dataStoreId: string, userId: string): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}/users/${userId}/access`;
        return this.apiService.sendRequest({
            method: "delete",
            endpoint,
        });
    }

    public async updateDataStoreAccess(dataStoreId: string, userId: string, accessLevel: number): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}/users/${userId}/access` +
        `?accessLevel=${encodeURIComponent(accessLevel)}`;
        return this.apiService.sendRequest({
            method: "patch",
            endpoint
        });
    }

    public async addContentToDataStore(dataStoreId: string, contentItem: object): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}/content`;
        return this.apiService.sendPollingRequest({
            method: "post",
            endpoint,
            data: contentItem,
            pollingInterval: 2000,
            maxPollingDuration: 900000
        });
    }

    public async createDataStoreFolder(dataStoreId: string, parentStorageId: string, name: string): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}/subfolder`;
        return this.apiService.sendRequest({
            method: "post",
            endpoint,
            data: {
                "name": name,
                "parentStorageId": parentStorageId
            },
        });
    }

    public async getDirectSubFoldersAndFiles(dataStoreId: string, storageId: string | null): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}/content` +
        `${this.paramBuilder({ parentStorageId: storageId }) }`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint
        });
    }

    // If the storageId of the content item is null, this uploads to the root level, otherwise uploads there.
    public async newAddContentToDataStore(dataStoreId: string, contentItem: object): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}/content`;
        return this.apiService.sendPollingRequest({
            method: "post",
            endpoint,
            data: contentItem,
            pollingInterval: 2000,
            maxPollingDuration: 900000
        });
    }

    public async fetchDataStoreItemMetadata(dataStoreId: string, contentId: string): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}/filenotes/${contentId}`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint
        });
    }

    public async updateDataStoreItemMetadata(dataStoreId: string, contentId: string, metadata: string): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}/filenotes/${contentId}`;
        return this.apiService.sendRequest({
            method: "put",
            endpoint,
            data: JSON.stringify(metadata),
        });
    }

    public async deleteDataStoreFolder(dataStoreId: string, storageId: string): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}/subfolder`;
        return this.apiService.sendRequest({
            method: "delete",
            endpoint,
            data: storageId,
        });
    }

    public async createDataStoreTextNote(dataStoreId: string, textNote: object): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}/textnote`;
        return this.apiService.sendRequest({
            method: "post",
            endpoint,
            data: textNote,
        });
    }

    public async fetchDataStoreTextNote(dataStoreId: string, contentId: string): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}/textnote/${contentId}`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async updateDataStoreTextNote(dataStoreId: string, contentId: string, textNote: object): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}/textnote/${contentId}`;
        return this.apiService.sendRequest({
            method: "put",
            endpoint,
            data: textNote,
        });
    }

    // TODO:
    // The frontend will need to be changed to match the output of GetDirectSubFoldersAndFiles() in DataCollectionController.cs,
    // which returns a Dictionary<string, object> that looks like:
    // {{ "Folders", List of DataCollection instances },
    //  { "Files", List of DataCollectionContentItem instances }}
    public async getDataStoreContents(dataStoreId: string): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}/content`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async renameDataStore(dataStoreId: string, newName: string): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}/rename`;
        return this.apiService.sendRequest({
            method: "put",
            endpoint,
            data: newName,
        });
    }

    // Removed the unnecessary param "dataStoreId: string"
    public async renameDataStoreContent(contentId: string, newName: string): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${contentId}/content/renameContentItemFileResource`;
        return this.apiService.sendRequest({
            method: "put",
            endpoint,
            data: newName,
        });
    }

    public async shareDataStore(dataStoreId: string, userId: string | undefined, userIdentifier: string | undefined, accessLevel : number = 3): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}/users`;
        return this.apiService.sendRequest({
            method: "post",
            endpoint,
            data: {
                dataStoreId: dataStoreId,
                userProfileId: userId,
                userProfile: {
                    identifier: userIdentifier
                },
                membership: accessLevel
            }
        });
    }

    public async getActiveUsers(dataStoreId: string): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}/activeUsers`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async updateDataStoreDescription(dataStoreId: string, newDescription: string): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}/description`;
        return this.apiService.sendRequest({
            method: "put",
            endpoint,
            data: newDescription,
        });
    }

    public async deleteDataStore(dataStoreId: string): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}`;
        return this.apiService.sendRequest({
            method: "delete",
            endpoint,
        });
    }

    public async searchDataStoresByName(dataStoreId: string, searchTerm: string, pageNumber: number = 1, pageSize: number = 10): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}/search${this.paramBuilder({ searchTerm, pageNumber, pageSize })}`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async exportDataStore(dataStoreId: string, path: string | null = null, contentItemId: string | null = null): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}/export${this.paramBuilder({ path, contentItemId })}`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async favoriteDataStore(dataStoreId: string): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}/favorite`;
        return this.apiService.sendRequest({
            method: "post",
            endpoint,
        });
    }

    public async unfavoriteDataStore(dataStoreId: string): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}/favorite`;
        return this.apiService.sendRequest({
            method: "delete",
            endpoint,
        });
    }

    public async getDataStoreAccesses(dataStoreId: string): Promise<any> {
        const endpoint = `/maestro/v3/DataCollections/${dataStoreId}/accesses`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }
}

class ModelsService {
    private apiService: SymphonyApiServicev3;

    constructor(apiService: SymphonyApiServicev3) {
        this.apiService = apiService;
    }

    public async getModels(): Promise<any> {
        const endpoint = `/maestro/v3/Models`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async createModel(model: any): Promise<any> {
        const endpoint = `/maestro/v3/models`;
        return this.apiService.sendRequest({
            method: 'post',
            endpoint,
            data: model,
        });
    }

    public async updateModel(id: string, model: any): Promise<any> {
        const endpoint = `/maestro/v3/models/${id}`;
        return this.apiService.sendRequest({
            method: 'put',
            endpoint,
            data: model,
        });
    }

    public async deleteModel(id: string): Promise<any> {
        const endpoint = `/maestro/v3/models/${id}`;
        return this.apiService.sendRequest({
            method: 'delete',
            endpoint,
        });
    }
}

class LicensingService {
    private apiService: SymphonyApiServicev3;

    constructor(apiService: SymphonyApiServicev3) {
        this.apiService = apiService;
    }

    public async isUserLicenseManager(): Promise<any> {
        const endpoint = `/v1/License/IsLicenseAdmin`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async isLicensedEnvironment(): Promise<any> {
        const endpoint = `/v1/License/IsLicensedEnvironment`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async getLicensedUsers(): Promise<any> {
        const endpoint = `/v1/License/Users`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async searchUsersToLicense(queryString: string): Promise<any> {
        const endpoint = `/v1/License/search?query=${queryString}`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async unlicenseUser(UPN: string): Promise<any> {
        const endpoint = `/v1/License/${UPN}`;
        return this.apiService.sendRequest({
            method: "delete",
            endpoint,
        });
    }

    public async licenseUser(UPN: string): Promise<any> {
        const endpoint = `/v1/License/${UPN}`;
        return this.apiService.sendRequest({
            method: "post",
            endpoint,
        });
    }

    public async getLicenseCount(): Promise<any> {
        const endpoint = `/v1/License/count`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async getUnusableUsers(): Promise<any> {
        const endpoint = `/v1/License/Unusable`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async amILicensed(): Promise<any> {
        const endpoint = `/v1/License`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }
}

class FactsService {
    private apiService: SymphonyApiServicev3;

    constructor(apiService: SymphonyApiServicev3) {
        this.apiService = apiService;
    }

    public async createFact(fact: any): Promise<any> {
        const endpoint = `/maestro/v3/Facts`;
        return this.apiService.sendRequest({
            method: 'post',
            endpoint,
            data: fact,
        });
    }

    public async getAllFacts(): Promise<any> {
        const endpoint = `/maestro/v3/Facts`;
        return this.apiService.sendRequest({
            method: 'get',
            endpoint,
        });
    }

    public async getFactById(factId: string): Promise<any> {
        const endpoint = `/maestro/v3/Facts/${factId}`;
        return this.apiService.sendRequest({
            method: 'get',
            endpoint,
        });
    }

    public async updateFact(factId: string, fact: any): Promise<any> {
        const endpoint = `/maestro/v3/Facts/${factId}`;
        return this.apiService.sendRequest({
            method: 'put',
            endpoint,
            data: fact,
        });
    }

    public async deleteFact(factId: string): Promise<any> {
        const endpoint = `/maestro/v3/Facts/${factId}`;
        return this.apiService.sendRequest({
            method: 'delete',
            endpoint,
        });
    }
}

class QuickActionsService {
    private apiService: SymphonyApiServicev3;
    private paramBuilder: ParamBuilder;

    constructor(apiService: SymphonyApiServicev3, paramBuilder: ParamBuilder) {
        this.apiService = apiService;
        this.paramBuilder = paramBuilder;
    }

    public async getQuickAction(quickActionId: string): Promise<any> {
        const endpoint = `/maestro/v3/quickactions/${quickActionId}`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async createQuickAction(quickAction: any): Promise<any> {
        const endpoint = `/maestro/v3/quickactions`;
        return this.apiService.sendRequest({
            method: "post",
            endpoint,
            data: quickAction
        });
    }

    public async updateQuickAction(quickActionId: string, quickAction: any): Promise<any> {
        const endpoint = `/maestro/v3/quickactions/${quickActionId}`;
        return this.apiService.sendRequest({
            method: "patch",
            endpoint,
            data: quickAction
        });
    }

    public async deleteQuickAction(quickActionId: string): Promise<any> {
        const endpoint = `/maestro/v3/quickactions/${quickActionId}`;
        return this.apiService.sendRequest({
            method: "delete",
            endpoint,
        });
    }

    public async getQuickActions(principleId: string): Promise<any> {
        const endpoint = `/maestro/v3/quickactions/principle/${principleId}`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }
}

class TemplateService {
    private apiService: SymphonyApiServicev3;
    private paramBuilder: ParamBuilder;

    constructor(apiService: SymphonyApiServicev3, paramBuilder: ParamBuilder) {
        this.apiService = apiService;
        this.paramBuilder = paramBuilder;
    }

    public async getTemplates(): Promise<any> {
        const endpoint = `/maestro/v3/Templates`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async getTemplate(templateId: string): Promise<any> {
        const endpoint = `/maestro/v3/Templates/${templateId}`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }

    public async createTemplate(template: any): Promise<any> {
        const endpoint = `/maestro/v3/Templates`;
        return this.apiService.sendRequest({
            method: "post",
            endpoint,
            data: template
        });
    }

    public async deleteTemplate(templateId: string): Promise<any> {
        const endpoint = `/maestro/v3/Templates/${templateId}`;
        return this.apiService.sendRequest({
            method: "delete",
            endpoint,
        });
    }

    public async getActiveUsers(templateId: string): Promise<any> {
        const endpoint = `/maestro/v3/Templates/${templateId}/activeUsers`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }



    public async shareTemplate(templateId: string, user: any, membership: number): Promise<any> {
        const endpoint = `/maestro/v3/Templates/${templateId}/users`;
        // Aligned with backend UserAccess class definition
        const userAccess = {
            userProfileId: user.userProfileId,
            accessIdentifier: user.identifier, // Changed from 'identifier' to 'accessIdentifier' to match backend
            accessType: "Template",
            membership: membership,
        };

        return this.apiService.sendRequest({
            method: "post",
            endpoint,
            data: userAccess
        });
    }

    public async removeAccess(templateId: string, userId: string): Promise<any> {
        const endpoint = `/maestro/v3/Templates/${templateId}/users/${userId}/access`;
        return this.apiService.sendRequest({
            method: "delete",
            endpoint,
        });
    }

    public async updateAccess(templateId: string, userId: string, accessLevel: number): Promise<any> {
        const endpoint = `/maestro/v3/Templates/${templateId}/users/${userId}/access`;

        const userAccess = {
            accessIdentifier: templateId,
            accessType: "Template",
            membership: accessLevel
        }
        return this.apiService.sendRequest({
            method: "patch",
            endpoint,
            data: userAccess
        });
    }

    public async toggleFavoriteTemplate(templateId: string): Promise<any> {
        const endpoint = `/maestro/v3/Templates/${templateId}/favorite`;
        return this.apiService.sendRequest({
            method: "post",
            endpoint,
        });
    }

    public async updateTemplate(templateId: string, template: any): Promise<any> {
        const endpoint = `/maestro/v3/Templates/${templateId}`;
        return this.apiService.sendRequest({
            method: "patch",
            endpoint,
            data: template
        });
    }

    public async addTemplateTags(templateId: string, tags: string[]): Promise<any> {
        const endpoint = `/maestro/v3/Templates/${templateId}/tags`;
        return this.apiService.sendRequest({
            method: "patch",
            endpoint,
            data: tags
        });
    }

    public async removeTemplateTags(templateId: string, tags: string[]): Promise<any> {
        const endpoint = `/maestro/v3/Templates/${templateId}/tags`;
        return this.apiService.sendRequest({
            method: "delete",
            endpoint,
            data: tags
        });
    }

    public async addTemplateSkills(templateId: string, skillIds: string[]): Promise<any> {
        const endpoint = `/maestro/v3/Templates/${templateId}/skills`;
        return this.apiService.sendRequest({
            method: "patch",
            endpoint,
            data: skillIds
        });
    }

    public async removeTemplateSkills(templateId: string, skillIds: string[]): Promise<any> {
        const endpoint = `/maestro/v3/Templates/${templateId}/skills`;
        return this.apiService.sendRequest({
            method: "delete",
            endpoint,
            data: skillIds
        });
    }

    public async updateDataStoreStatus(templateId: string, datastoreId: string, isEnabled: boolean): Promise<any> {
        const endpoint = `/maestro/v3/Templates/${templateId}/datastores/${datastoreId}`;
        return this.apiService.sendRequest({
            method: "patch",
            endpoint,
            data: isEnabled
        });
    }

    public async createTemplateSession(templateId: string, templateComponents: any = []): Promise<any> {
        const endpoint = `/maestro/v3/Templates/${templateId}/sessions`;

        return this.apiService.sendRequest({
            method: "post",
            endpoint,
            data: templateComponents
        })
    }

    //not used
    public async updateTemplateSession(templateId: string, sessionId: string, templateComponents: any): Promise<any> {
        const endpoint = `/maestro/v3/Templates/${templateId}/sessions/${sessionId}`;

        return this.apiService.sendRequest({
            method: "patch",
            endpoint,
            data: templateComponents
        })
    }

    public async addFileToSession(templateId: string, sessionId: string, file: any): Promise<any> {
        const endpoint = `/maestro/v3/Templates/${templateId}/sessions/${sessionId}/resources`;

        return this.apiService.sendPollingRequest({
            method: "post",
            endpoint,
            data: file,
            pollingInterval: 500,
            maxPollingDuration: 900000
        });
    }

    public async addFileToTemplate(templateId: string, file: any): Promise<any> {
        const endpoint = `/maestro/v3/Templates/${templateId}/resources`;

        return this.apiService.sendPollingRequest({
            method: "post",
            endpoint,
            data: file,
            pollingInterval: 500,
            maxPollingDuration: 900000
        });
    }

    public async deleteFileFromTemplate(templateId: string, resourceId: string): Promise<any> {
        const endpoint = `/maestro/v3/Templates/${templateId}/resources/${resourceId}`;

        return this.apiService.sendRequest({
            method: "delete",
            endpoint
        });
    }

    public async removeFileFromSession(templateId: string, sessionId: string, resourceId: string): Promise<any> {
        const endpoint = `/maestro/v3/Templates/${templateId}/sessions/${sessionId}/resources/${resourceId}`;

        return this.apiService.sendRequest({
            method: "delete",
            endpoint
        })
    }

    public async executeSession(templateId: string, sessionId: string): Promise<any> {
        const endpoint = `/maestro/v3/Templates/${templateId}/sessions/${sessionId}/execute`;

        return this.apiService.sendPollingRequest({
            method: "post",
            endpoint,
            pollingInterval: 500
        });
    }

}

class IntegrationsService {
    private apiService: SymphonyApiServicev3;

    constructor(apiService: SymphonyApiServicev3) {
        this.apiService = apiService;
    }

    public async getIntegrations(): Promise<any> {
        const endpoint = `/framework/v3/Integrations`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint
        })
    }

    public async getIntegration(integId: string): Promise<any> {
        const endpoint = `/framework/v3/Integrations/${integId}`;

        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        })
    }

    public async createIntegration(integration: any): Promise<any> {
        const endpoint = `/framework/v3/Integrations`;

        return this.apiService.sendRequest({
            method: "post",
            endpoint,
            data: integration
        })
    }

    public async updateIntegration(integId: string, integration: any): Promise<any> {
        const endpoint = `/framework/v3/Integrations/${integId}`;

        return this.apiService.sendRequest({
            method: "patch",
            endpoint,
            data: integration
        })
    }

    public async deleteIntegration(integId: string): Promise<any> {
        const endpoint = `/framework/v3/Integrations/${integId}`;

        return this.apiService.sendRequest({
            method: "delete",
            endpoint,
        })
    }

    public async getConfigurations(): Promise<any> {
        const endpoint = `/framework/v3/Integrations/Configurations`;

        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        })
    }

    public async getConfiguration(configId: string): Promise<any> {
        const endpoint = `/framework/v3/Integrations/Configurations/${configId}`;

        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        })
    }

    public async getIntegrationFields(integId: string): Promise<any> {
        const endpoint = `/framework/v3/Integrations/${integId}/fields`;

        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        })
    }

    public async updateIntegrationFields(integId: string, fields: any): Promise<any> {
        const endpoint = `/framework/v3/Integrations/${integId}/fields`;

        return this.apiService.sendRequest({
            method: "patch",
            endpoint,
            data: fields
        })
    }

    public async getIntegrationTools(integId: string): Promise<any> {
        const endpoint = `/framework/v3/Integrations/${integId}/tools/`;

        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        })
    }

    public async getIntegrationTool(integId: string, toolId: string,): Promise<any> {
        const endpoint = `/framework/v3/Integrations/${integId}/tools/${toolId}`;

        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        })
    }

    public async createIntegrationTool(integId: string, tool: any): Promise<any> {
        const endpoint = `/framework/v3/Integrations/${integId}/tools`;

        return this.apiService.sendRequest({
            method: "post",
            endpoint,
            data: tool
        })
    }

    public async updateIntegrationTool(integId: string, toolId: string, tool: any): Promise<any> {
        const endpoint = `/framework/v3/Integrations/${integId}/tools/${toolId}`;

        return this.apiService.sendRequest({
            method: "patch",
            endpoint,
            data: tool
        })
    }

    public async deleteIntegrationTool(integId: string, toolId: string): Promise<any> {
        const endpoint = `/framework/v3/Integrations/${integId}/tools/${toolId}`;

        return this.apiService.sendRequest({
            method: "delete",
            endpoint,
        })
    }
}



class ResourcesService {
    private apiService: SymphonyApiServicev3;

    constructor(apiService: SymphonyApiServicev3) {
        this.apiService = apiService;
    }

    /**
     * Fetches a resource as a blob for authenticated access.
     * @param resourceId The resource ID to fetch
     * @returns Promise with blob data
     */
    public async getResource(resourceId: string): Promise<Blob> {
        const endpoint = `/framework/v3/resources/${resourceId}`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
            returnBlob: true // Important: tells axios to return blob data
        });
    }

    /**
     * Fetches resource metadata without downloading the file.
     * @param resourceId The resource ID
     * @returns Promise with resource metadata
     */
    public async getResourceMetadata(resourceId: string): Promise<any> {
        const endpoint = `/framework/v3/resources/${resourceId}/metadata`;
        return this.apiService.sendRequest({
            method: "get",
            endpoint,
        });
    }
}


/*
    This function doesn't make any sense, but is a placeholder so I don't need to type the whole thing out 10 times.

    public async templateFunc(field: any): Promise<any> {
        const endpoint = `/maestro/v3/${field}`;

}*/
// override content type
/*
public async postFormData(endpoint: string, data: any): Promise<any> {
    const formHeaders = this.createHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    try {
      const response = await this.apiClient.post(endpoint, qs.stringify(data), formHeaders);
      return response.data;
    } catch (error) {
      throw error;
    }
  }


  public async fetchProtectedData(endpoint: string, params?: any): Promise<any> {
    try {
      const response = await this.apiClient.get(endpoint, { params });
      return response.data;
    } catch (error) {
      // The interceptor will handle retries for eligible errors.
      // Here you can handle errors that should not be retried, or perform additional error processing.
      throw error;
    }
  }


  // Example usage of apiService.sendRequest to send form data
const formData = new URLSearchParams();
formData.append('key1', 'value1');
formData.append('key2', 'value2');

apiService.sendRequest({
  method: 'post',
  endpoint: '/submit-form',
  data: formData.toString(), // Convert URLSearchParams to string for Axios
  extraHeaders: { 'Content-Type': 'application/x-www-form-urlencoded' },
})
.then(response => {
  console.log('Form submitted successfully', response);
})
.catch(error => {
  console.error('Error submitting form', error);
});

// Example usage of apiService.sendRequest to upload a file
const fileInput = document.querySelector('input[type="file"]'); // Assuming there's a file input element in your HTML
const file = fileInput.files[0];

const fileFormData = new FormData();
fileFormData.append('file', file);
fileFormData.append('description', 'This is a file upload.');

apiService.sendRequest({
  method: 'post',
  endpoint: '/upload-file',
  data: fileFormData,
  // Content-Type is automatically set to multipart/form-data by the browser
})
.then(response => {
  console.log('File uploaded successfully', response);
})
.catch(error => {
  console.error('Error uploading file', error);
});


  */
